<template>
  <div class="smtp-wrap">

    <!-- Processing overlay (send test email) -->
    <transition name="fade">
      <div class="processing-overlay" v-if="processing">
        <div class="processing-card">
          <i class="fa fa-circle-o-notch fa-spin"></i>
          <div class="processing-title">Sending test email…</div>
          <div class="processing-sub">Please wait while we verify the connection</div>
        </div>
      </div>
    </transition>

    <!-- Initial load -->
    <div class="tab-loading" v-if="loading">
      <i class="fa fa-spinner fa-spin"></i> Loading configuration…
    </div>

    <template v-else>
      <form @submit.stop.prevent>

        <div class="form-group">
          <label>Server <span class="req">*</span></label>
          <input type="text" class="field" v-model="server"
            :class="{ 'field-err': errors['server'] }" placeholder="e.g. smtp.gmail.com" />
          <span v-if="errors['server']" class="err-msg">{{ errors["server"][0] }}</span>
        </div>

        <div class="form-group">
          <label>Port <span class="req">*</span></label>
          <input type="number" class="field field-sm" v-model="port"
            :class="{ 'field-err': errors['port'] }" placeholder="465" />
          <span v-if="errors['port']" class="err-msg">{{ errors["port"][0] }}</span>
        </div>

        <div class="form-group">
          <label>Username <span class="req">*</span></label>
          <input type="email" class="field" v-model="username"
            :class="{ 'field-err': errors['username'] }" placeholder="you@example.com" />
          <span v-if="errors['username']" class="err-msg">{{ errors["username"][0] }}</span>
        </div>

        <div class="form-group">
          <label>Password <span class="req">*</span></label>
          <div class="pw-wrap" :class="{ 'pw-wrap-err': errors['password'] }">
            <input :type="password_type" class="field pw-field" v-model="password"
              placeholder="••••••••" />
            <button type="button" class="pw-eye" @click="showPassword()">
              <i :class="password_type === 'password' ? 'fa fa-eye' : 'fa fa-eye-slash'"></i>
            </button>
          </div>
          <span v-if="errors['password']" class="err-msg">{{ errors["password"][0] }}</span>
        </div>

        <div class="form-group">
          <label>Connection Encryption</label>
          <div class="ssl-segment">
            <button type="button" class="seg-btn" :class="{ active: !ssl_tls }" @click="ssl_tls = false">
              <i class="fa fa-lock"></i> SSL
            </button>
            <button type="button" class="seg-btn" :class="{ active: ssl_tls }" @click="ssl_tls = true">
              <i class="fa fa-shield"></i> TLS
            </button>
          </div>
          <div class="ssl-hint">
            {{ ssl_tls ? 'TLS — encrypts using STARTTLS (recommended for port 587)' : 'SSL — full encryption from connection start (recommended for port 465)' }}
          </div>
        </div>

      </form>

      <div class="form-footer">
        <button class="btn-test" @click="testConnection()" ref="kt_test_smtp_email_connection">
          <i class="fa fa-envelope"></i> Send Test Email
        </button>
        <button class="btn-save" @click="onSubmit()" ref="kt_save_configure">
          <i class="fa fa-check"></i> Save
        </button>
      </div>
    </template>

  </div>
</template>

<script>
import {
  FETCH_SMTP_OUTGOING_EMAIL_CONFIGURE,
  STORE_SMTP_OUTGOING_EMAIL_CONFIGURE,
  CHECK_SMTP_CONNECTION
} from "@/core/services/store/actions.type";

export default {
  data() {
    return {
      loading: true,
      processing: false,
      errors: [],
      id: 0,
      server: "",
      port: 465,
      username: "",
      password: "",
      ssl_tls: false,
      password_type: "password"
    };
  },
  created() {
    this.$store.dispatch(FETCH_SMTP_OUTGOING_EMAIL_CONFIGURE).then(data => {
      if (data.status == true) {
        this.id       = data.data.id;
        this.username = data.data.username;
        this.password = data.data.password;
        this.server   = data.data.server;
        this.port     = data.data.port;
        this.ssl_tls  = !!data.data.connect_with_ssl_tls;
      }
      this.loading = false;
    }).catch(() => { this.loading = false; });
  },
  methods: {
    showPassword() {
      this.password_type = this.password_type === "password" ? "text" : "password";
    },
    async testConnection() {
      const { value: email } = await this.$swal.fire({
        title: "Testing SMTP Email Connection",
        input: "email",
        inputPlaceholder: "Email address",
        confirmButtonText: "Send Email",
        cancelButtonText: "Cancel",
        showCancelButton: true,
        showCloseButton: true,
        allowOutsideClick: false,
        showLoaderOnConfirm: true
      });
      if (email) {
        this.processing = true;
        this.$store.dispatch(CHECK_SMTP_CONNECTION, {
          server: this.server,
          username: this.username,
          password: this.password,
          port: this.port,
          connect_with_ssl_tls: this.ssl_tls,
          to_email: email
        }).then(data => {
          this.processing = false;
          this.$toastr.s(data.msg);
        }).catch(err => {
          this.processing = false;
          let msg = err.msg;
          if (msg.includes("Authentication Failed")) msg = "Username or Password is invalid";
          else if (msg.includes("getaddrinfo failed")) msg = "Server is invalid";
          this.$toastr.e(msg);
        });
      }
    },
    onSubmit() {
      this.errors = [];
      const btn = this.$refs["kt_save_configure"];
      btn.disabled = true;
      btn.classList.add("spinner", "spinner-light", "spinner-right");
      this.$store.dispatch(STORE_SMTP_OUTGOING_EMAIL_CONFIGURE, {
        id_: this.id,
        server: this.server,
        username: this.username,
        password: this.password,
        port: this.port,
        connect_with_ssl_tls: this.ssl_tls
      }).then(data => {
        btn.classList.remove("spinner", "spinner-light", "spinner-right");
        btn.disabled = false;
        if (this.id == 0) this.id = data.id;
        this.$toastr.s(data.msg);
      }).catch(err => {
        btn.classList.remove("spinner", "spinner-light", "spinner-right");
        btn.disabled = false;
        this.errors = err.error;
      });
    }
  }
};
</script>

<style lang="scss" scoped>
$font:   'Segoe UI', sans-serif;
$blue:   #389ad4;
$green:  #276749;
$orange: #F58020;
$dark:   #0d1b3e;
$border: #dde2ec;

.smtp-wrap { position: relative; font-family: $font; }

/* Loading */
.tab-loading {
  display: flex; align-items: center; gap: 10px;
  padding: 40px 0; color: #8a94a6; font-size: 13px;
  i { color: $blue; font-size: 18px; }
}

/* Processing overlay */
.processing-overlay {
  position: fixed; inset: 0; z-index: 1050;
  background: rgba(13,27,62,0.45);
  backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
}
.processing-card {
  background: #fff;
  border-radius: 16px;
  padding: 36px 44px;
  text-align: center;
  box-shadow: 0 8px 32px rgba(13,27,62,0.18);
  i { font-size: 32px; color: $blue; margin-bottom: 14px; }
}
.processing-title { font-size: 15px; font-weight: 700; color: $dark; margin-bottom: 6px; }
.processing-sub   { font-size: 12px; color: #8a94a6; }

.fade-enter-active, .fade-leave-active { transition: opacity 0.2s; }
.fade-enter, .fade-leave-to { opacity: 0; }

/* Form */
.form-group { margin-bottom: 18px; }

label {
  display: block;
  font-size: 11px; font-weight: 700;
  color: #4a5568;
  text-transform: uppercase; letter-spacing: 0.4px;
  margin-bottom: 6px;
}
.req { color: #e53e3e; }

.field {
  display: block; width: 100%;
  height: 38px;
  border: 1.5px solid $border;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 13px; font-family: $font; color: $dark;
  background: #fff;
  transition: border-color 0.15s;
  box-shadow: none; outline: none;
  &:focus { border-color: $blue; }
  &.field-err { border-color: #e53e3e; }
  &.field-sm { width: 140px; }
}

/* Password */
.pw-wrap {
  display: flex;
  border: 1.5px solid $border;
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.15s;
  &:focus-within { border-color: $blue; }
  &.pw-wrap-err { border-color: #e53e3e; }
}
.pw-field {
  border: none; border-radius: 0; flex: 1;
  height: 38px; padding: 0 12px;
  font-size: 13px; font-family: $font; color: $dark;
  outline: none; background: #fff;
}
.pw-eye {
  width: 40px; height: 38px;
  border: none; background: #fff;
  color: #9aa0ae; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  font-size: 14px; flex-shrink: 0;
  &:hover { color: $blue; }
}

.err-msg { font-size: 11px; color: #e53e3e; margin-top: 3px; display: block; }

/* SSL/TLS segment */
.ssl-segment {
  display: inline-flex;
  border: 1.5px solid $border;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 8px;
}
.seg-btn {
  height: 34px; padding: 0 20px;
  border: none; background: #fff;
  color: #6b7280; font-size: 13px; font-weight: 600; font-family: $font;
  cursor: pointer; display: flex; align-items: center; gap: 6px;
  transition: background 0.15s, color 0.15s;
  & + & { border-left: 1.5px solid $border; }
  i { font-size: 11px; }
  &.active { background: $blue; color: #fff; border-color: $blue; }
  &:not(.active):hover { background: #f0f8fd; color: $blue; }
}
.ssl-hint {
  font-size: 11px; color: #8a94a6;
  background: #f0f8fd;
  border: 1px solid #c8e6f7;
  border-radius: 6px;
  padding: 6px 10px;
}

/* Footer */
.form-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 24px;
  margin-top: 8px;
  border-top: 1px solid #f0f3fa;
}

.btn-test {
  display: inline-flex; align-items: center; gap: 7px;
  height: 38px; padding: 0 20px; border-radius: 8px;
  border: 1.5px solid $orange; background: transparent;
  color: $orange; font-size: 13px; font-weight: 600; font-family: $font;
  cursor: pointer; transition: background 0.15s;
  i { font-size: 12px; }
  &:hover { background: #fff5ec; }
  &:disabled { opacity: 0.6; cursor: default; }
}

.btn-save {
  display: inline-flex; align-items: center; gap: 7px;
  height: 38px; padding: 0 24px; border-radius: 8px;
  border: none; background: $green;
  color: #fff; font-size: 13px; font-weight: 600; font-family: $font;
  cursor: pointer; transition: background 0.15s;
  i { font-size: 12px; }
  &:hover { background: #1d5230; }
  &:disabled { opacity: 0.6; cursor: default; }
}
</style>
